/**
 * ChartTooltip - the hover card for a multi-series price chart.
 *
 * Recharts lists series in the order they were added, which on a five line
 * chart means the tooltip order and the visual order disagree. Reading
 * "Sorghum $178.50" at the top while sorghum is the bottom line, and cocoa at
 * $1,950 is the top one, makes the reader match values to lines by arithmetic
 * instead of by eye.
 *
 * So rows are sorted by value, descending, which is exactly the order the
 * lines sit in at the hovered moment. The order changes as the lines cross,
 * which is the point: it always matches what is on screen.
 *
 * Each row also carries its line's colour as a dot. The shared tooltip recipe
 * paints every row the same translucent white, so colour was the one cue that
 * would have linked a row to its line and it was being thrown away. The dot
 * carries it instead of the text, because several of the series colours are
 * dark navy and would disappear against this tooltip's background.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeMode, MONO } from '../../context/ThemeContext';

function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
  nameFormatter,
  highlightKey,
}) {
  const { palette } = useThemeMode();
  if (!active || !payload || payload.length === 0) return null;

  const rows = payload
    .filter((p) => p && p.value != null)
    .slice()
    .sort((a, b) => Number(b.value) - Number(a.value));

  if (rows.length === 0) return null;

  return (
    <Box
      sx={{
        backgroundColor: palette.tooltipBg,
        border: `1px solid ${palette.tooltipBorder}`,
        borderRadius: '10px',
        padding: '10px 12px',
        minWidth: 180,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: palette.accentLight, mb: 0.75 }}>
        {labelFormatter ? labelFormatter(label) : label}
      </Typography>
      {rows.map((row) => {
        const isPrimary = highlightKey && row.dataKey === highlightKey;
        return (
          <Box
            key={row.dataKey ?? row.name}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: row.color || row.stroke || palette.accent,
                // A faint ring, because several series colours are dark navy
                // and a dark dot on this dark tooltip is no dot at all.
                border: '1px solid rgba(255,255,255,0.4)',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: '0.76rem',
                color: 'rgba(255,255,255,0.75)',
                fontWeight: isPrimary ? 700 : 400,
                flex: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {nameFormatter ? nameFormatter(row.name, row) : row.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: '0.76rem',
                color: '#fff',
                fontWeight: isPrimary ? 700 : 500,
                whiteSpace: 'nowrap',
              }}
            >
              {valueFormatter ? valueFormatter(row.value, row) : row.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default ChartTooltip;
